/**
 * persistErrors — traduzir a recusa do Postgres para uma frase acionável.
 *
 * ─── O BUG QUE ORIGINOU ESTE ARQUIVO (D14, achado em aparelho) ──────────────
 *
 * Uma conta recém-criada não tinha empresa, e a RLS de team accounts exige
 * `company_id` gravável em todo INSERT (#4). O tour de boas-vindas levava a
 * pessoa a `/quotes/new`, ela preenchia cliente, item e preço — e o Salvar
 * devolvia, num toast vermelho, a mensagem CRUA do Postgres, em inglês:
 *
 *     new row violates row-level security policy for table "quotes"
 *
 * Nada dizia o que fazer. A causa-raiz foi corrigida no banco (a primeira
 * empresa nasce com a conta, migração `20260804153410`), mas o vazamento da
 * mensagem é um defeito PRÓPRIO: a mesma frase apareceria para um `viewer`, ou
 * para quem tem o papel revogado no meio da sessão. Erro de banco nunca é texto
 * de interface.
 *
 * Puro de propósito: dá para testar cada tradução sem React nem rede.
 */

/** Erro do supabase-js/PostgREST, ou qualquer coisa que o `catch` pegar. */
interface PostgrestLike {
  code?: string | null;
  message?: string | null;
}

function asPostgrest(error: unknown): PostgrestLike {
  if (!error || typeof error !== 'object') {
    return { message: typeof error === 'string' ? error : null };
  }
  const e = error as Record<string, unknown>;
  return {
    code: typeof e.code === 'string' ? e.code : null,
    message: typeof e.message === 'string' ? e.message : null,
  };
}

/**
 * Violação de RLS. O código `42501` é o sinal confiável; o texto é a rede para
 * quando o erro chega embrulhado (o `NewQuotePage` relança como `Error`, então
 * o `code` se perde no caminho).
 */
export function isRlsViolation(error: unknown): boolean {
  const { code, message } = asPostgrest(error);
  if (code === '42501') return true;
  return /row-level security policy/i.test(message ?? '');
}

export const NO_COMPANY_MESSAGE =
  'Você ainda não tem uma empresa cadastrada. Crie a sua em Ajustes → Empresas para começar a salvar.';

const RLS_MESSAGE =
  'Você não tem permissão para salvar nesta empresa. Confira seu acesso em Ajustes → Empresas.';

/**
 * A frase que vai para o toast.
 *
 * `hasCompany` separa os dois motivos de uma recusa de RLS, que para o usuário
 * são problemas COMPLETAMENTE diferentes: "falta criar sua empresa" (e há o que
 * fazer agora) × "seu acesso não permite" (e não há). Dizer o segundo para quem
 * está no primeiro manda a pessoa procurar um problema de permissão que não
 * existe.
 *
 * `fallback` é usado quando o erro não é de RLS: aí a mensagem do servidor é
 * mais informativa do que qualquer genérica nossa.
 */
export function describePersistError(
  error: unknown,
  { hasCompany, fallback }: { hasCompany: boolean; fallback: string },
): string {
  if (isRlsViolation(error)) {
    return hasCompany ? RLS_MESSAGE : NO_COMPANY_MESSAGE;
  }
  const { message } = asPostgrest(error);
  return message && message.trim() ? message : fallback;
}
